<template>
  <main class="ndfl-certificate">
    <div class="ndfl-certificate__container">
      <h1 class="ndfl-certificate__title">Справка для налоговой</h1>
      <ndfl-certificate-form
        v-if="isFormLoaded"
        v-bind:identification-code-options="identificationCodeOptions"
        v-bind:input-errors="inputErrors"
        v-bind:invoices="invoices"
        v-bind="certificateProps"
        v-on:submit="onSubmit"
      >
        <template v-slot:actions>
          <button class="ndfl-certificate__button-submit" type="submit">
            Сформировать
          </button>
        </template>
      </ndfl-certificate-form>
    </div>
  </main>
</template>

<script>

import notifierMixin from '@/mixins/notifierMixin';
import NdflCertificateForm from '@/components/NdflCertificateForm.vue';
import { Maybe } from 'monet';

/**
 * @description Компонент страницы создания справки НДФЛ.
 */
export default {
  components: {
    NdflCertificateForm,
  },
  computed: {
    reportingYear() {
      return this.$route.query.year;
    },
    reportingType() {
      return this.$route.query.type;
    },
    patientId() {
      return this.$route.params.id;
    },
    selectedClinicId() {
      return this.$route.query.clinic_id || null;
    },
    /**
     * @description Данные справки.
     * @returns {any}
     */
    certificateProps() {
      return this.certificate.fold({})((certificate) => {
        const { guardian, patient } = certificate;
        return {
          initialAmountByExpensive: certificate.amount_by_expensive,
          initialAmountBySimple: certificate.amount_by_simple,
          initialCertificateNumber: certificate.certificate_number,
          initialCorrectionNumber: '0',
          initialGuardianBirthDate: guardian.birth_date,
          initialGuardianIdentificationCode: guardian.identification_code,
          initialGuardianIdentificationSerialAndNumber: guardian.identification_serial_and_number,
          initialGuardianIdentificationWhenIssued: guardian.identification_when_issued,
          initialGuardianName: guardian.name,
          initialGuardianInn: guardian.inn,
          initialGuardianPatronymic: guardian.patronymic,
          initialGuardianSurname: guardian.surname,
          initialIsSinglePerson: certificate.is_single_person,
          initialIssuedDate: certificate.issued_date,
          initialPatientBirthDate: patient.birth_date,
          initialPatientIdentificationCode: patient.identification_code,
          initialPatientIdentificationSerialAndNumber: patient.identification_serial_and_number,
          initialPatientIdentificationWhenIssued: patient.identification_when_issued,
          initialPatientInn: patient.inn,
          initialPatientName: patient.name,
          initialPatientPatronymic: patient.patronymic,
          initialPatientSurname: patient.surname,
          initialReportingYear: this.reportingYear,
          initialReportingType: this.reportingType,
          selectedClinicId: this.selectedClinicId,
          signerId: this.$route.query.signer_id || null,
        };
      });
    },
  },
  created() {
    this.fetchFillers(this.reportingYear);
  },
  data() {
    return {
      certificate: Maybe.none(),
      identificationCodeOptions: [],
      inputErrors: {},
      invoices: [],
      isAwaitRequest: false,
      isFormLoaded: false,
    };
  },
  methods: {
    /**
     * @description Получить заполнители.
     * @param {number} reportingYear Отчетный год.
     * @returns {void}
     */
    fetchFillers(reportingYear) {
      this.isFormLoaded = false;
      const onResponse = ({ fillers, identificationCodeOptions, invoices }) => {
        this.certificate = Maybe.fromEmpty(fillers);
        this.identificationCodeOptions = identificationCodeOptions;
        this.invoices = invoices;
        this.isFormLoaded = true;
      };
      this.$registryNdflCertificateGateway
        .viewFillers(
          this.patientId,
          this.selectedClinicId,
          reportingYear,
          this.reportingType,
        )
        .then((result) => result.forEach(onResponse));
    },
    /**
     * @description Обработать отправку формы.
     * @param {FormNdflCertificateRequest} request Запрос.
     * @returns {void}
     */
    onSubmit(request) {
      this.isAwaitRequest = true;
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.openRegistry(request.getIssuedDate());
      };
      this.$registryNdflCertificateGateway
        .formCertificate(this.patientId, request)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => {
          this.isAwaitRequest = false;
        });
    },
    /**
     * @description Открыть реестр.
     * @param {string} issuedDate Дата выдачи.
     * @returns {void}
     */
    openRegistry(issuedDate) {
      this.$router.replace({
        name: 'ClinicPanelRegistryNdflCertificateList',
        params: { issuedDate },
      });
    },
  },
  mixins: [notifierMixin],
  name: 'ClinicPanelNdflCertificateCreation',
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";

.ndfl-certificate {
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  &__container {
    width: 1187px;
    margin: 0 auto 0 0;
    padding: 38px 20px 200px 23px;
  }
  &__title {
    color: #000000;
    margin: 0 0 27px 0;
  }
  &__button {
    &-submit {
      max-width: 400px;
      @include buttons.btn-blue(100%, 56px, 20px);
      margin: 40px 0 0 0;

      &:disabled {
        background-color: var(--unfocus-color);
      }
    }
  }
}
@media screen and (max-width: 480px) {
  .ndfl-certificate {
    &__container {
      padding: 38px 10px 140px;
    }
  }
}
</style>
